import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { formatEbvAccuracyDisplay } from '@/hooks/publicSearch/ebv/ebvColumns';

/** Aligns CSV/Excel/print with on-screen EBV display toggles (split stacked cell into columns). */
export type PublicSearchGridExportOptions = {
  showEBVs?: boolean;
  showEBVAccuracy?: boolean;
  showPercentiles?: boolean;
  isBlackRedAngusStrain?: boolean;
};

const EBV_LEAF_FIELD_RE = /^EBV(\d+)$/;
const EBV_INDEX_COLUMN_CLASS = 'ebv-highlighted';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function csvEscape(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

function cellToExportString(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if (
      'value' in o &&
      (typeof o.value === 'string' ||
        typeof o.value === 'number' ||
        o.value instanceof Date)
    ) {
      if (o.value instanceof Date && !isNaN(o.value.getTime())) {
        return o.value.toISOString().slice(0, 10);
      }
      return o.value === '' || o.value == null ? '' : String(o.value);
    }
  }
  return '';
}

/**
 * String for export/print: avoids [object Object] from MUI CSV when only renderCell is set.
 */
function formatCellForExport(
  api: GridApiPro,
  row: GridRowModel,
  col: GridColDef,
): string {
  try {
    const f = api.getRowFormattedValue(row, col);
    if (f != null && f !== '') {
      const fs = String(f);
      if (fs !== '[object Object]') return fs.replace(/\r?\n/g, ' ');
    }
  } catch {
    /* column may not support formatted value */
  }

  let v: unknown;
  try {
    v = api.getRowValue(row, col);
  } catch {
    v = undefined;
  }
  if (v === undefined) v = (row as Record<string, unknown>)[col.field];
  return cellToExportString(v).replace(/\r?\n/g, ' ');
}

type ExportColumnSpec =
  | { kind: 'standard'; col: GridColDef }
  | { kind: 'ebvValue'; col: GridColDef }
  | {
      kind: 'ebvAcc';
      ebvCol: GridColDef;
      accField: string;
      header: string;
    }
  | {
      kind: 'ebvPct';
      ebvCol: GridColDef;
      pctField: string;
      header: string;
    };

function isEbvIndexColumn(col: GridColDef): boolean {
  const headerClassName = col.headerClassName;
  return (
    typeof headerClassName === 'string' &&
    headerClassName.split(/\s+/).includes(EBV_INDEX_COLUMN_CLASS)
  );
}

function buildExportColumnSpecs(
  columns: GridColDef[],
  opts?: PublicSearchGridExportOptions,
): ExportColumnSpec[] {
  const showEbv = opts?.showEBVs ?? false;
  const showAcc = opts?.showEBVAccuracy ?? false;
  const showPct = opts?.showPercentiles ?? false;
  const expandEbv = showEbv || showAcc || showPct;

  const specs: ExportColumnSpec[] = [];
  for (const col of columns) {
    const field = String(col.field);
    const m = EBV_LEAF_FIELD_RE.exec(field);
    if (m && isEbvIndexColumn(col)) {
      // Indexes show a value and a percentile (but no accuracy) on screen, so
      // mirror that in the export by emitting a paired percentile column.
      const label = String(col.headerName ?? field).replace(/\r?\n/g, ' ');
      const n = m[1];
      if (showEbv) {
        specs.push({ kind: 'ebvValue', col });
      }
      if (showPct) {
        specs.push({
          kind: 'ebvPct',
          ebvCol: col,
          pctField: `EBV${n}_PERCENTILE`,
          header: `${label} (percentile)`,
        });
      }
    } else if (m && expandEbv) {
      const label = String(col.headerName ?? field).replace(/\r?\n/g, ' ');
      const n = m[1];
      if (showEbv) {
        specs.push({ kind: 'ebvValue', col });
      }
      if (showAcc) {
        specs.push({
          kind: 'ebvAcc',
          ebvCol: col,
          accField: `EBV${n}_ACC`,
          header: `${label} (accuracy %)`,
        });
      }
      if (showPct) {
        specs.push({
          kind: 'ebvPct',
          ebvCol: col,
          pctField: `EBV${n}_PERCENTILE`,
          header: `${label} (percentile)`,
        });
      }
    } else {
      specs.push({ kind: 'standard', col });
    }
  }
  return specs;
}

function formatExportCell(
  api: GridApiPro,
  row: GridRowModel,
  spec: ExportColumnSpec,
  opts?: PublicSearchGridExportOptions,
): string {
  switch (spec.kind) {
    case 'standard':
    case 'ebvValue':
      return formatCellForExport(api, row, spec.col);
    case 'ebvAcc': {
      const raw = (row as Record<string, unknown>)[spec.accField];
      if (raw == null || raw === '') return '';
      return formatEbvAccuracyDisplay(raw, {
        isBlackRedAngusStrain: opts?.isBlackRedAngusStrain,
      });
    }
    case 'ebvPct': {
      const raw = (row as Record<string, unknown>)[spec.pctField];
      if (raw == null || raw === '') return '';
      return String(raw).replace(/\r?\n/g, ' ');
    }
    default:
      return '';
  }
}

/**
 * Visible leaf columns and row values aligned with grid data (not MUI's CSV serializer).
 * When EBV display toggles are on, splits each EBV column into separate value / accuracy / percentile columns.
 */
export function buildExportTableFromGridApi(
  api: GridApiPro,
  exportOptions?: PublicSearchGridExportOptions,
): {
  headers: string[];
  rows: string[][];
} {
  const columns = api
    .getVisibleColumns()
    .filter((c) => c.field && !String(c.field).startsWith('__'));

  const specs = buildExportColumnSpecs(columns, exportOptions);

  const headers = specs.map((s) => {
    if (s.kind === 'ebvAcc' || s.kind === 'ebvPct') return s.header;
    return String(s.col.headerName ?? s.col.field).replace(/\r?\n/g, ' ');
  });

  const rowIds = api.getSortedRowIds();
  const rows = rowIds.map((id) => {
    const row = api.getRow(id);
    if (!row) return specs.map(() => '');
    return specs.map((spec) => formatExportCell(api, row, spec, exportOptions));
  });

  return { headers, rows };
}

/** Base name without file extension (MUI exportAs appends .csv itself). */
export function publicSearchExportBaseFileName(
  societyCode: string | undefined,
) {
  const date = new Date().toISOString().slice(0, 10);
  const prefix = (societyCode ?? 'search').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${prefix}_search_${date}`;
}

/**
 * Prints the same table as CSV/Excel via a hidden iframe (not window.open — no pop-up blocker).
 * MUI `exportDataAsPrint` clones grid DOM into an iframe; that often renders blank while the
 * grid API still has correct data — this path uses `buildExportTableFromGridApi` only.
 */
export function printPublicSearchGridIframe(
  api: GridApiPro,
  documentTitle: string,
  exportOptions?: PublicSearchGridExportOptions,
): void {
  const { headers, rows } = buildExportTableFromGridApi(api, exportOptions);
  const thead = `<tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr>`;
  const tbody = rows
    .map(
      (r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`,
    )
    .join('');

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtml(documentTitle)}</title>
<style>
*{box-sizing:border-box}
body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;margin:16px;color:#111;background:#fff}
h1{font-size:14px;font-weight:600;margin:0 0 12px}
table{border-collapse:collapse;width:100%;font-size:11px}
th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}
th{background:#f5f5f5;font-weight:600}
@media print{@page{margin:12mm}body{margin:0}h1{font-size:12px}}
</style></head><body><h1>${escapeHtml(documentTitle)}</h1><table><thead>${thead}</thead><tbody>${tbody}</tbody></table></body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = documentTitle;
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const idoc = iframe.contentDocument;
  if (!win || !idoc) {
    iframe.remove();
    throw new Error('Print frame not available');
  }

  const removeFrame = () => {
    iframe.remove();
  };

  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  idoc.documentElement.replaceWith(
    idoc.importNode(parsedDocument.documentElement, true),
  );

  const runPrint = () => {
    win.addEventListener('afterprint', removeFrame, { once: true });
    window.setTimeout(removeFrame, 120_000);
    win.focus();
    win.print();
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(runPrint);
  });
}

export function downloadPublicSearchGridCsv(
  api: GridApiPro,
  baseFileName: string,
  exportOptions?: PublicSearchGridExportOptions,
): void {
  const { headers, rows } = buildExportTableFromGridApi(api, exportOptions);
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((r) => r.map(csvEscape).join(',')),
  ];
  const csv = lines.join('\n');
  const blob = new Blob([`\uFEFF${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const safeBase = baseFileName.replace(/\.csv$/i, '');
  const name = `${safeBase}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadPublicSearchGridExcel(
  api: GridApiPro,
  baseFileName: string,
  exportOptions?: PublicSearchGridExportOptions,
): Promise<void> {
  const { headers, rows } = buildExportTableFromGridApi(api, exportOptions);
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Search results', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  sheet.addRow(headers);
  rows.forEach((r) => sheet.addRow(r));
  const buf = await workbook.xlsx.writeBuffer();
  const safeBase = baseFileName.replace(/\.xlsx$/i, '');
  const name = `${safeBase}.xlsx`;
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
